import React from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import { TRAINING_MESSAGES } from '../../constants/training';
import type { TrainingVideo } from '../../types/training';

interface VideoSidebarProps {
    videos: TrainingVideo[];
    categories: string[];
    activeCategory: string;
    searchQuery: string;
    selectedVideoId: string | null;
    onSelectVideo: (id: string) => void;
    onSearchChange: (q: string) => void;
    onCategoryChange: (cat: string) => void;
}

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const VideoSidebar: React.FC<VideoSidebarProps> = ({
    videos,
    categories,
    activeCategory,
    searchQuery,
    selectedVideoId,
    onSelectVideo,
    onSearchChange,
    onCategoryChange,
}) => {
    const { t } = useTranslate();

    return (
        <aside className="flex flex-col h-full bg-white border-r border-gray-200">
            {/* Header */}
            <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    🎓 {t(TRAINING_MESSAGES.PAGE_TITLE)}
                </h2>

                {/* Search */}
                <div className="relative mb-3">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={e => onSearchChange(e.target.value)}
                        placeholder={t(TRAINING_MESSAGES.SEARCH_PLACEHOLDER)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D31145]/30 focus:border-[#D31145]/40 transition-all"
                    />
                </div>

                {/* Category filter pills */}
                <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t('Video categories')}>
                    <button
                        role="tab"
                        type="button"
                        aria-selected={activeCategory === 'all'}
                        onClick={() => onCategoryChange('all')}
                        className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 flex-shrink-0 ${activeCategory === 'all'
                                ? 'bg-[#D31145] text-white shadow-sm'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        {t(TRAINING_MESSAGES.ALL_CATEGORIES)}
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            role="tab"
                            type="button"
                            aria-selected={activeCategory === cat}
                            onClick={() => onCategoryChange(cat)}
                            className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 flex-shrink-0 ${activeCategory === cat
                                    ? 'bg-[#D31145] text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {t(cat)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Video list */}
            <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {videos.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                        <p className="text-gray-400 text-sm">{t(TRAINING_MESSAGES.NO_VIDEOS)}</p>
                    </div>
                ) : (
                    <div role="listbox" aria-label={t(TRAINING_MESSAGES.PAGE_TITLE)}>
                        {videos.map(video => {
                            const isSelected = video.id === selectedVideoId;
                            const thumbnail = video.thumbnailFilename ? `/${video.thumbnailFilename}` : null;
                            return (
                                <button
                                    key={video.id}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => onSelectVideo(video.id)}
                                    className={`w-full text-left px-4 py-3 border-l-3 transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${isSelected
                                            ? 'border-l-[#D31145] bg-red-50/60'
                                            : 'border-l-transparent'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Thumbnail */}
                                        <div className="flex-shrink-0 w-16 h-10 rounded overflow-hidden bg-gray-200 relative">
                                            {thumbnail ? (
                                                <img
                                                    src={thumbnail}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M8 5v14l11-7z" />
                                                    </svg>
                                                </div>
                                            )}
                                            {/* Play/pause overlay */}
                                            <div className={`absolute inset-0 flex items-center justify-center ${isSelected ? 'bg-black/30' : 'bg-transparent'
                                                }`}>
                                                {isSelected && (
                                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                        <rect x="6" y="4" width="4" height="16" rx="1" />
                                                        <rect x="14" y="4" width="4" height="16" rx="1" />
                                                    </svg>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <h3 className={`text-sm font-medium line-clamp-1 ${isSelected ? 'text-[#D31145]' : 'text-gray-900'
                                                }`}>
                                                {video.title}
                                            </h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-gray-400 truncate">
                                                    {video.category}
                                                </span>
                                                <span className="text-xs text-gray-300">·</span>
                                                <span className="text-xs text-gray-400 flex-shrink-0">
                                                    {formatDuration(video.durationSeconds)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer count */}
            <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 text-center">
                    {videos.length} {videos.length === 1 ? 'video' : 'videos'}
                </p>
            </div>
        </aside>
    );
};

export default VideoSidebar;
